import { ComponentProps, Fragment } from 'react'
import { css } from '@emotion/react'
import MissionPC from '@images/MissionPC'
import MissionSP from '@images/MissionSP'
import Mission from '@ui/organisms/index/Mission'
import Product from '@ui/organisms/index/Product'
import Recruit from '@ui/organisms/index/Recruit'
import NewsList from '@ui/organisms/NewsList'
import { MyTheme, theme } from '@ui/styles/theme'
import ButtonLink from '@ui/atoms/ButtionLink'
import Title from '@ui/atoms/Title'
import FirstImage from '@images/first_view@2x.png'
import { useTranslation } from 'react-i18next'
import { Languages } from '@/types/languages'

type DataType = {
  data:
    | Queries.TopContentsQuery
    | Queries.TopContentsEnQuery
    | Queries.TopContentsThQuery
  lang: Languages
}

const getNewsDataInLanguage = (language: Languages, data: DataType['data']) => {
  switch (language) {
    case 'ja':
      return (data as Queries.TopContentsQuery).allMicrocmsNews
    case 'en':
      return (data as Queries.TopContentsEnQuery).allMicrocmsNewsEn
    case 'th':
      return (data as Queries.TopContentsThQuery).allMicrocmsNewsTh
    default: {
      const _: never = language
      throw new Error('unreachable!')
    }
  }
}

export const IndexPage = ({ data, lang = 'ja' }: DataType) => {
  const styles = createStyles(theme)
  const { t } = useTranslation()
  const { i18n } = useTranslation()
  const newsData = getNewsDataInLanguage(
    i18n.language as Languages,
    data
  ).edges.map(edge => edge.node) as ComponentProps<typeof NewsList>['news']

  return (
    <Fragment>
      <div css={styles.container}>
        <img src={FirstImage} alt="cities" css={styles.containerImage} />
        <h1 css={styles.title}>
          <div css={styles.isSP}>
            <MissionSP />
          </div>
          <div css={styles.isTab}>
            <MissionPC />
          </div>
        </h1>
      </div>
      <main>
        <div css={styles.mainInner}>
          <section css={[styles.section, styles.mission]}>
            <Mission lang={lang} />
          </section>
          <section css={[styles.section, styles.products]}>
            <Product lang={lang} />
          </section>
          {lang === 'ja' ? (
            <Fragment>
              <section css={[styles.section, styles.recruit]}>
                <div css={styles.recruitInner}>
                  <Recruit />
                </div>
              </section>
              <section css={[styles.section, styles.news]}>
                <Title title={t('お知らせ')} />
                <div css={styles.subTitle}>News</div>
                <div css={styles.newsList}>
                  <NewsList news={newsData} />
                </div>
                <div css={styles.button}>
                  <ButtonLink path="/news/" text={t('お知らせ一覧へ')} />
                </div>
              </section>
            </Fragment>
          ) : (
            <Fragment>
              <section css={[styles.section, styles.news]}>
                <Title title={t('お知らせ')} />
                <div css={styles.newsList}>
                  <NewsList news={newsData} />
                </div>
                <div css={styles.button}>
                  <ButtonLink path="/en/news/" text={t('お知らせ一覧へ')} />
                </div>
              </section>
            </Fragment>
          )}
        </div>
      </main>
    </Fragment>
  )
}

const createStyles = (theme: MyTheme) => {
  return {
    container: css`
      position: relative;
      width: 100vw;
      height: calc(100vh - 114px);
      min-height: 400px;
      max-height: calc(100vh - 114px);
      margin: auto;
      ${theme.mq.isTablet} {
        height: 640px;
      }
    `,
    containerImage: css`
      position: relative;
      width: 100vw;
      height: calc(100vh - 114px);
      min-height: 400px;
      max-height: calc(100vh - 114px);
      object-fit: cover;
      object-position: left;
      opacity: 0.7;
      ${theme.mq.isTablet} {
        height: 640px;
      }
    `,
    title: css`
      position: absolute;
      top: calc(50% - 170px);
      left: calc(50% - 90px);
      ${theme.mq.isTablet} {
        top: calc(50% - 180px);
        left: calc(50% - 105px);
      }
      ${theme.mq.isDesktop} {
        left: 0;
        right: 0;
        width: 932px;
        margin: 0 auto;
      }
    `,
    subTitle: css`
      margin-top: 12px;
      font-size: 14px;
      font-family: Montserrat;
      color: #007b69;
    `,
    mainInner: css`
      position: relative;
      padding: 0 32px;
      ${theme.mq.isTablet} {
        max-width: 1152px;
        min-height: calc(100vh - 380px);
        margin: 0 auto;
        padding: 0;
      }
    `,
    section: css`
      padding: 32px 0;
      ${theme.mq.isTablet} {
        padding: 32px;
      }
      ${theme.mq.isDesktop} {
        padding: 0;
      }
    `,
    mission: css`
      position: absolute;
      top: -130px;
      right: 0;
      padding: 0;
      ${theme.mq.isTablet} {
        padding: 32px 0 32px 32px;
      }
      ${theme.mq.isDesktop} {
        right: -80px;
        width: 640px;
        padding: 32px 0;
      }
    `,
    products: css`
      padding-top: 350px;
      ${theme.mq.isTablet} {
        padding-top: 270px;
      }
      ${theme.mq.isDesktop} {
        width: 640px;
        padding-top: 185px;
      }
    `,
    recruit: css`
      ${theme.mq.isDesktop} {
        display: flex;
        margin-right: -64px;
        justify-content: end;
      }
    `,
    recruitInner: css`
      ${theme.mq.isDesktop} {
        width: 960px;
      }
    `,
    news: css`
      ${theme.mq.isTablet} {
        margin-top: 280px;
      }
      ${theme.mq.isDesktop} {
        margin-top: 32px;
      }
    `,
    newsList: css`
      margin-top: 32px;
    `,
    button: css`
      text-align: right;
    `,
    isSP: css`
      display: block;
      ${theme.mq.isTablet} {
        display: none;
      }
    `,
    isTab: css`
      display: none;
      ${theme.mq.isTablet} {
        display: block;
      }
    `
  }
}
